// How each executive log is shown on screen: which column dates a row, which
// columns can be filtered, and which money columns are totalled in the cards
// above the table. Totals only add up the stored values the server sent; they
// recalculate nothing (docs/specs/executive-logs.md, "Money stays server-side").

import type { ExecLogName } from "./execLogColumns";

export interface ExecLogTotal {
  key: string;
  label: string;
  /** Count the rows instead of summing, e.g. "GiftPasses claimed". */
  countWhere?: { key: string; value: string };
  /** Shown in pesos. Distances and counts are plain numbers. */
  peso?: boolean;
}

export interface ExecLogView {
  /** The column a date filter reads. None for a balance snapshot. */
  dateKey?: string;
  /** Columns offered as filter chips, in toolbar order. */
  facetKeys: string[];
  totals: ExecLogTotal[];
}

export const EXEC_LOG_VIEWS: Record<ExecLogName, ExecLogView> = {
  bookings: {
    dateKey: "booking_date",
    facetKeys: ["payment_method", "status"],
    totals: [
      { key: "summary_total", label: "Summary total", peso: true },
      { key: "amount_paid", label: "Amount paid", peso: true },
      { key: "online_processing", label: "Online processing", peso: true },
    ],
  },
  services: {
    facetKeys: ["payment_method", "status", "service_name"],
    totals: [
      { key: "service_list_price", label: "Service list price", peso: true },
      { key: "therapist_share", label: "Therapist share", peso: true },
      { key: "surcharge_share", label: "Surcharge share", peso: true },
    ],
  },
  dispatches: {
    dateKey: "dispatch_date",
    facetKeys: ["rider_tier", "status"],
    totals: [{ key: "total_km", label: "Tracked distance (km)" }],
  },
  giftpasses: {
    dateKey: "generated_on",
    facetKeys: ["status", "claim_state", "campaign"],
    totals: [
      { key: "value_php", label: "Total value", peso: true },
      {
        key: "claim_state",
        label: "Claimed",
        countWhere: { key: "claim_state", value: "claimed" },
      },
    ],
  },
  giftpass_orders: {
    dateKey: "order_date",
    facetKeys: ["status", "package_name", "payment_method", "delivery_status"],
    totals: [
      { key: "total_amount", label: "Total amount", peso: true },
      { key: "net_amount", label: "Net amount", peso: true },
      { key: "total_passes", label: "Passes" },
    ],
  },
  client_wallet: {
    dateKey: "entry_date",
    facetKeys: ["entry_type"],
    totals: [{ key: "amount", label: "Net movement", peso: true }],
  },
  partner_wallet: {
    facetKeys: ["role"],
    totals: [
      { key: "posted_earnings", label: "Posted earnings", peso: true },
      { key: "cash_held", label: "Cash held", peso: true },
      { key: "available_balance", label: "Available balance", peso: true },
    ],
  },
  rider_sessions: {
    dateKey: "computed_at",
    facetKeys: ["rider_tier"],
    totals: [
      { key: "total_php", label: "Total pay", peso: true },
      { key: "dispatch_count", label: "Dispatches" },
    ],
  },
  partner_transactions: {
    dateKey: "entry_date",
    facetKeys: ["role", "entry_type"],
    totals: [{ key: "amount", label: "Net amount", peso: true }],
  },
};

export type DatePreset = "all" | "today" | "7d" | "30d" | "month" | "last_month" | "year";

export interface DateRange {
  from: string;
  to: string;
}

function isoDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** The from/to days a preset stands for, in local time. "all" is open on both ends. */
export function presetRange(preset: DatePreset, now = new Date()): DateRange {
  if (preset === "all") return { from: "", to: "" };
  const today = isoDay(now);
  if (preset === "today") return { from: today, to: today };
  if (preset === "7d" || preset === "30d") {
    const start = new Date(now);
    start.setDate(start.getDate() - (preset === "7d" ? 6 : 29));
    return { from: isoDay(start), to: today };
  }
  if (preset === "last_month") {
    return {
      from: isoDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
      to: isoDay(new Date(now.getFullYear(), now.getMonth(), 0)),
    };
  }
  if (preset === "year") return { from: isoDay(new Date(now.getFullYear(), 0, 1)), to: today };
  return { from: isoDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
}

/** True when a row's date (a day or a timestamp) falls inside the range. A row with no date only passes an open range. */
export function inRange(value: unknown, range: DateRange): boolean {
  if (!range.from && !range.to) return true;
  if (value === null || value === undefined || value === "") return false;
  const day = String(value).slice(0, 10);
  if (range.from && day < range.from) return false;
  if (range.to && day > range.to) return false;
  return true;
}

/** Sum a money or distance column; values that aren't numbers are skipped. */
export function sumColumn(rows: Record<string, unknown>[], key: string): number {
  let total = 0;
  for (const row of rows) {
    const value = Number(row[key]);
    if (row[key] !== null && row[key] !== "" && Number.isFinite(value)) total += value;
  }
  return Math.round(total * 100) / 100;
}

export interface FacetOption {
  value: string;
  label: string;
}

const BOOKING_STATUS: FacetOption[] = [
  { value: "pending", label: "Pending" },
  { value: "awaiting_therapist", label: "Awaiting therapist" },
  { value: "awaiting_rider", label: "Awaiting rider" },
  { value: "dispatched", label: "Dispatched" },
  { value: "en_route_to_client", label: "On the way" },
  { value: "arrived", label: "Arrived" },
  { value: "in_session", label: "In session" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const YES_NO: FacetOption[] = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const RANK: FacetOption[] = [
  { value: "bronze", label: "Bronze" },
  { value: "silver", label: "Silver" },
  { value: "gold", label: "Gold" },
  { value: "diamond", label: "Diamond" },
];

/**
 * The filter choices for each filterable column, from the database's own
 * value lists, so a filter shows its choices before any row exists. A value
 * that appears in the data but not here is still offered (see facetOptions).
 */
export const FACET_OPTIONS: Record<string, FacetOption[]> = {
  status: BOOKING_STATUS,
  payment_method: [
    { value: "online", label: "Online (QR Ph / Maya)" },
    { value: "cash", label: "Cash" },
    { value: "credits", label: "Wallet" },
    { value: "giftpass", label: "GiftPass" },
    { value: "giftpass_partial", label: "GiftPass + top-up" },
  ],
  rider_tier: RANK,
  role: [
    { value: "therapist", label: "Therapist" },
    { value: "rider", label: "Rider" },
  ],
  claim_state: [
    { value: "claimed", label: "Claimed" },
    { value: "unclaimed", label: "Unclaimed" },
    { value: "other", label: "Expired or invalid" },
  ],
  // Trip statuses have no fixed list yet: offered from the rows themselves.
  "dispatches.status": [],
  "giftpass_orders.status": [
    { value: "pending_payment", label: "Waiting for payment" },
    { value: "awaiting_transfer", label: "Waiting for transfer" },
    { value: "paid", label: "Paid" },
    { value: "expired", label: "Expired" },
    { value: "failed", label: "Failed" },
    { value: "cancelled", label: "Cancelled" },
  ],
  "giftpass_orders.payment_method": [
    { value: "qrph", label: "QR Ph" },
    { value: "bank_transfer", label: "Bank transfer" },
    { value: "ewallet", label: "E-wallet" },
    { value: "cash", label: "Cash" },
    { value: "other", label: "Other" },
  ],
  "giftpass_orders.source": [
    { value: "online", label: "Online" },
    { value: "manual", label: "Manual" },
  ],
  "giftpass_orders.delivery_status": [
    { value: "to_print", label: "To print" },
    { value: "printed", label: "Printed" },
    { value: "delivered", label: "Delivered" },
  ],
  "giftpass_orders.has_proof": YES_NO,
  "giftpass_orders.physical": YES_NO,
  "giftpass_orders.codes_sent": YES_NO,
  "giftpass_orders.payment_verification": [
    { value: "paymongo", label: "PayMongo" },
    { value: "staff", label: "Staff" },
  ],
  "giftpasses.status": [
    { value: "active", label: "Active" },
    { value: "redeemed", label: "Redeemed" },
    { value: "expired", label: "Expired" },
    { value: "invalidated", label: "Invalidated" },
    { value: "revalidation_required", label: "Needs revalidation" },
  ],
  "client_wallet.entry_type": [
    { value: "topup", label: "Top-up" },
    { value: "spend", label: "Spent on a booking" },
    { value: "cancellation_refund", label: "Cancellation refund" },
    { value: "system_refund", label: "System refund" },
    { value: "reversal", label: "Reversal" },
  ],
  "partner_transactions.entry_type": [
    { value: "payroll_earning", label: "Earning" },
    { value: "cash_collected", label: "Cash collected" },
    { value: "cash_handoff_in", label: "Cash received" },
    { value: "cash_handoff_out", label: "Cash handed over" },
    { value: "cash_retained", label: "Cash retained" },
    { value: "payout_paid", label: "Payout paid" },
  ],
};

function titleCase(value: string): string {
  const spaced = value.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function knownOptions(log: string, key: string): FacetOption[] {
  return FACET_OPTIONS[`${log}.${key}`] ?? FACET_OPTIONS[key] ?? [];
}

/** The fixed choices for a column, plus any other value the rows hold. */
export function facetOptions(
  log: string,
  key: string,
  rows: Record<string, unknown>[],
): FacetOption[] {
  const known = knownOptions(log, key);
  const seen = new Set(known.map((option) => option.value));
  const extra: FacetOption[] = [];
  for (const row of rows) {
    const value = row[key];
    if (value === null || value === undefined || value === "") continue;
    const text = String(value);
    if (seen.has(text)) continue;
    seen.add(text);
    extra.push({ value: text, label: titleCase(text) });
  }
  return [...known, ...extra.sort((a, b) => a.label.localeCompare(b.label))];
}

/** The readable label for a stored value, for the table cell. The download keeps the stored value. */
export function displayValue(log: string, key: string, value: unknown): string {
  if (value === null || value === undefined) return "";
  const text = String(value);
  return knownOptions(log, key).find((option) => option.value === text)?.label ?? text;
}
